import { Component, Inject, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { MfaService } from '../../../auth/services/mfa.service';
import { SnackService } from '../snack/snack.service';

@Component({
  selector: 'app-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrls: ['./mfa-setup.component.scss']
})
export class MfaSetupComponent implements OnInit {
  fb = inject(FormBuilder);
  _translate = inject(TranslateService);
  mfaService = inject(MfaService);
  snackService = inject(SnackService);

  currentUserId = JSON.parse(localStorage.getItem('CURRENT_USER') || '{}')?.userId;

  mfaEnabled = false;
  setupMode = false;
  disableMode = false;
  qrCodeImage = '';
  secret = '';
  loading = true;

  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: { userId?: number; userName?: string; dialogMode?: boolean }) {}

  setupForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6}$')]]
  });

  disableForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6}$')]]
  });

  get managedUserId(): number | null {
    return this.dialogData?.userId ?? null;
  }

  get managedUserName(): string {
    return this.dialogData?.userName || '';
  }

  get dialogMode(): boolean {
    return !!this.dialogData?.dialogMode;
  }

  get isManagingAnotherUser(): boolean {
    return !!this.managedUserId && this.managedUserId !== this.currentUserId;
  }

  ngOnInit(): void {
    this.loadStatus();
  }

  loadStatus(): void {
    this.mfaService.getStatus(this.managedUserId).subscribe({
      next: (res) => {
        this.mfaEnabled = res?.data === true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  startSetup(): void {
    this.setupMode = true;
    this.mfaService.setup(this.managedUserId).subscribe({
      next: (res) => {
        this.secret = res?.data?.secret;
        this.qrCodeImage = res?.data?.qrCodeImage;
      },
      error: () => {
        this.snackService.showSnack(this._translate.instant('MFA.SETUP_ERROR'), 'error');
        this.setupMode = false;
      }
    });
  }

  cancelSetup(): void {
    this.setupMode = false;
    this.qrCodeImage = '';
    this.secret = '';
    this.setupForm.reset();
  }

  verifySetup(): void {
    if (this.setupForm.invalid) return;

    const code = this.setupForm.value.code;
    this.mfaService.verifySetup(this.secret, code, this.managedUserId).subscribe({
      next: () => {
        this.mfaEnabled = true;
        this.setupMode = false;
        this.qrCodeImage = '';
        this.secret = '';
        this.setupForm.reset();
        this.snackService.showSnack(this._translate.instant('MFA.ENABLED_SUCCESS'), 'success');
      },
      error: (error) => {
        this.snackService.showSnack(
          error?.error?.message || this._translate.instant('MFA.INVALID_CODE'), 'error'
        );
      }
    });
  }

  startDisable(): void {
    this.disableMode = true;
  }

  cancelDisable(): void {
    this.disableMode = false;
    this.disableForm.reset();
  }

  confirmDisable(): void {
    if (this.disableForm.invalid) return;

    const code = this.disableForm.value.code;
    this.mfaService.disable(code, this.managedUserId).subscribe({
      next: () => {
        this.mfaEnabled = false;
        this.disableMode = false;
        this.disableForm.reset();
        this.snackService.showSnack(this._translate.instant('MFA.DISABLED_SUCCESS'), 'success');
      },
      error: (error) => {
        this.snackService.showSnack(
          error?.error?.message || this._translate.instant('MFA.INVALID_CODE'), 'error'
        );
      }
    });
  }

  resetManagedUserMfa(): void {
    if (!this.managedUserId) {
      return;
    }

    const confirmed = window.confirm(this._translate.instant('MFA.RESET_CONFIRMATION'));
    if (!confirmed) {
      return;
    }

    this.mfaService.reset(this.managedUserId).subscribe({
      next: () => {
        this.mfaEnabled = false;
        this.setupMode = false;
        this.disableMode = false;
        this.qrCodeImage = '';
        this.secret = '';
        this.setupForm.reset();
        this.disableForm.reset();
        this.snackService.showSnack(this._translate.instant('MFA.RESET_SUCCESS'), 'success');
      },
      error: (error) => {
        this.snackService.showSnack(
          error?.error?.message || this._translate.instant('MFA.SETUP_ERROR'),
          'error'
        );
      }
    });
  }
}
